import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, FormInput } from '@praktio/common-ui';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { forgotPassword } from '../../ducks/users';
import UnauthenticatedWrapper from '../common/UnauthenticatedWrapper/UnauthenticatedWrapper';
import {
  ErrorCodes,
  ForgotPasswordPageFields,
  NetworkErrorFormWarning,
  SiteKey,
} from '../../utils/constants';

const useQuery = () => {
  const location = useLocation();
  return new URLSearchParams(location.search);
};

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const query = useQuery();
  const isExpired = query.get('expired') === '1';
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [token, setToken] = useState<string | undefined>();
  const [captchaError, setCaptchaError] = useState<string | undefined>();
  const [submitted, setSubmitted] = useState(false);

  interface ForgotPasswordFormIProps {
    field?: string;
    value?: string;
  }
  const onFormChange = ({ field, value }: ForgotPasswordFormIProps) => {
    if (field) {
      if (field === ForgotPasswordPageFields.email) {
        setEmailError(undefined);
        setEmail(value || '');
      }
      if (field === ForgotPasswordPageFields.captcha) {
        setCaptchaError(undefined);
        setToken(value || undefined);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let currentEmailError = emailError;
    let currentCaptchaError = captchaError;

    // Set error if email is empty
    if (!email || email === '') {
      currentEmailError = 'This field cannot be blank.';
      setEmailError(currentEmailError);
    }

    // Set error if recaptcha not completed
    if (!token || token === '') {
      currentCaptchaError = 'Please complete the ReCAPTCHA.';
      setCaptchaError(currentCaptchaError);
    }

    // Send reset if token and email are present and there are no errors for either
    if (token && email && !currentEmailError && !currentCaptchaError) {
      try {
        await dispatch(forgotPassword({ email, token }));
        setSubmitted(true);
      } catch (e) {
        const error = e as IError;
        console.error(error);
      }

      recaptchaRef.current?.reset();
    }
  };

  return (
    <UnauthenticatedWrapper formName={`${isExpired ? 'Reset' : 'Forgot'} Password`}>
      <Helmet>
        <title>Forgot Password - Praktio</title>
      </Helmet>
      <div className="ForgotPassword">
        {!submitted && (
          <>
            {captchaError && (
              <div className="alert alert-danger" role="alert">
                <div className="alert-title">Form Error</div>
                <div className="alert-body">{captchaError}</div>
              </div>
            )}
            {isExpired && (
              <div className="alert alert-info" role="alert">
                <div className="alert-title">Expired Password</div>
                <div className="alert-body">
                  Your password has expired. Please submit your email to receive a reset link.
                </div>
              </div>
            )}
            <Form
              className="forgot-password-form"
              id="forgot-password-form"
              onSubmit={(e: any) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <FormInput
                label="Email"
                placeholder="Email"
                fieldName="email"
                value={email}
                onChange={(e: any) =>
                  onFormChange({
                    field: ForgotPasswordPageFields.email,
                    value: e.target.value,
                  })
                }
                error={emailError}
                required
              />
              <div className="ReCAPTCHA-container mb-3">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={SiteKey}
                  onChange={(token) => {
                    onFormChange({
                      field: ForgotPasswordPageFields.captcha,
                      value: token || undefined,
                    });
                  }}
                />
              </div>
              <Button
                onClick={(e: any) => {
                  handleSubmit(e);
                }}
                form="forgot-password-form"
                type="submit"
                btnStyle="primary"
              >
                SEND LINK
              </Button>
            </Form>
          </>
        )}
        {submitted && (
          <div className="alert alert-success" role="alert">
            <div className="alert-title">Link Sent</div>
            <div className="alert-body">Password reset link sent to {email}.</div>
          </div>
        )}
      </div>
    </UnauthenticatedWrapper>
  );
};

export default ForgotPassword;
